import { StatCard } from './CustomerStatCard';
import { CustomerIdentityEditor } from './CustomerIdentityEditor';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, Plus, Building2, Loader2, ChevronDown, ChevronUp, Pencil, Trash2,
  Phone, Mail, IndianRupee, FileText, Users as UsersIcon,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';
import { resolveRoleKey } from '@/pages/dashboard/roleLayouts';
import { CreateCustomerModal } from './CreateCustomerModal';

interface Customer {
  id: string;
  name: string;
  type: string;
  industry?: string;
  phone?: string;
  email?: string;
  gstin?: string;
  pan?: string;
  stateCode?: string;
  billingAddress?: any;
  shippingAddress?: any;
  creditLimit?: number;
  paymentTerms?: string;
  rating?: number;
  createdAt: string;
  _count?: { contacts?: number; deals?: number; invoices?: number };
}

function formatINR(n?: number | null) {
  if (!n) return '-';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
}

export function CustomersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [editing, setEditing] = useState<Customer | null>(null);
  const user = useAuthStore((s) => s.user);
  const roleKey = useMemo(() => resolveRoleKey(user as never), [user]);
  const canCreate =
    roleKey === 'ADMIN' ||
    roleKey === 'SALES_MANAGER' ||
    roleKey === 'SALES_EXECUTIVE' || roleKey === 'CRM_OPERATIONS';

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const totalPages = Math.ceil(total / limit);

  const [search, setSearch] = useState(location.state?.search ?? '');
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [createOpen, setCreateOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {
        type: 'customer',
        page: String(page),
        limit: String(limit),
      };
      if (search) params.search = search;
      if (sortField) params.sortBy = sortField;
      if (sortDir) params.sortOrder = sortDir;
      const res = await api.get('/accounts', { params });
      setCustomers(res.data.data?.accounts ?? res.data.data ?? []);
      setTotal(res.data.data?.pagination?.total ?? res.data.pagination?.total ?? res.data.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [search, sortField, sortDir, page]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  useEffect(() => {
    const id = location.state?.recordId;
    if (roleKey !== 'CRM_OPERATIONS' || typeof id !== 'string') return;
    let current = true;
    api.get(`/accounts/${encodeURIComponent(id)}`).then(response => {
      if (current) setEditing(response.data.data);
    }).catch(() => { if (current) setError('Could not open the customer for correction.'); });
    return () => { current = false; };
  }, [roleKey, location.state?.recordId]);
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const handleCreated = (customer: any) => {
    // Optimistic prepend so the user sees the new row immediately
    if (customer?.id) {
      setCustomers((prev) => [customer, ...prev]);
      setTotal((t) => t + 1);
    }
    setToast(`Customer “${customer?.name ?? 'new'}” created`);
    window.setTimeout(() => setToast(null), 3200);
    // Refetch in background for canonical ordering / counts
    fetchCustomers();
  };

  const handleDelete = async (id: string) => {
    if (!canCreate) return;
    if (!confirm('Are you sure you want to delete this customer?')) return;
    try {
      await api.delete(`/accounts/${id}`);
      fetchCustomers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete customer');
    }
  };

  const totalCustomers = total;
  const withGSTIN = customers.filter(c => c.gstin).length;
  const totalDeals = customers.reduce((s, c) => s + (c._count?.deals || 0), 0);
  const totalContacts = customers.reduce((s, c) => s + (c._count?.contacts || 0), 0);

  return (
    <div
      className="aura-root"
      style={{ padding: 24, background: 'var(--ivory-0)', minHeight: '100%' }}
    >
      {editing && <CustomerIdentityEditor customer={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchCustomers(); }} />}
{/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1
            style={{
              fontFamily: 'var(--ff-serif)',
              fontSize: 28,
              fontWeight: 500,
              letterSpacing: -0.02,
              color: 'var(--ink-0)',
              marginBottom: 2,
            }}
          >
            Customers
          </h1>
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            Manage your customer accounts
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => setCreateOpen(true)}
            className="btn btn-primary"
            style={{ height: 36, padding: '0 14px' }}
          >
            <Plus size={14} />
            Add Customer
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard icon={<Building2 size={18} />} value={totalCustomers} label="Total customers" accent="forest" />
        <StatCard icon={<UsersIcon size={18} />} value={totalContacts} label="Total contacts" accent="info" />
        <StatCard icon={<FileText size={18} />} value={totalDeals} label="Active deals" accent="saffron" />
        <StatCard icon={<IndianRupee size={18} />} value={withGSTIN} label="With GSTIN" accent="saffron" />
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative" style={{ maxWidth: 380 }}>
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-3)' }} />
          <input
            type="text"
            placeholder="Search customers by name, email, or GSTIN…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="ivory-input"
            style={{ paddingLeft: 32 }}
          />
        </div>
      </div>

      {/* Table */}
      <div
        className="card"
        style={{ overflow: 'hidden', padding: 0 }}
      >
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={20} className="animate-spin" style={{ color: 'var(--saffron-600)' }} />
            <span className="ml-2 text-sm" style={{ color: 'var(--ink-3)' }}>Loading customers…</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20" style={{ color: 'var(--danger)' }}>
            <p className="text-sm">{error}</p>
            <button onClick={fetchCustomers} className="mt-2 btn btn-outline btn-sm">Retry</button>
          </div>
        ) : customers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20" style={{ color: 'var(--ink-3)' }}>
            <Building2 size={36} className="mb-2" />
            <p className="text-sm">No customers yet</p>
            <p className="text-xs">Add your first customer to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                    <span className="row gap-1 center">Name <SortIcon field="name" /></span>
                  </th>
                  <th>Industry</th>
                  <th>Contact</th>
                  <th>GSTIN</th>
                  <th>Payment</th>
                  <th style={{ textAlign: 'right' }}>Credit limit</th>
                  <th style={{ textAlign: 'center' }}>Contacts</th>
                  <th style={{ textAlign: 'center' }}>Deals</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id} onClick={() => roleKey === 'CRM_OPERATIONS' ? setEditing(c) : navigate(`/accounts/${c.id}`)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div style={{ fontWeight: 500, color: 'var(--ink-0)' }}>{c.name}</div>
                      {c.stateCode && (
                        <div className="text-xs" style={{ color: 'var(--ink-3)' }}>State: {c.stateCode}</div>
                      )}
                    </td>
                    <td style={{ color: 'var(--ink-2)' }}>{c.industry || '-'}</td>
                    <td>
                      <div className="space-y-0.5">
                        {c.phone && (
                          <div className="row gap-1" style={{ color: 'var(--ink-2)' }}>
                            <Phone size={11} style={{ color: 'var(--ink-3)' }} />
                            <span className="text-xs">{c.phone}</span>
                          </div>
                        )}
                        {c.email && (
                          <div className="row gap-1" style={{ color: 'var(--ink-2)' }}>
                            <Mail size={11} style={{ color: 'var(--ink-3)' }} />
                            <span className="text-xs">{c.email}</span>
                          </div>
                        )}
                        {!c.phone && !c.email && <span className="text-xs" style={{ color: 'var(--ink-3)' }}>-</span>}
                      </div>
                    </td>
                    <td className="mono text-xs" style={{ color: 'var(--ink-2)' }}>{c.gstin || '-'}</td>
                    <td>
                      {c.paymentTerms ? (
                        <span className="chip chip-blue">{c.paymentTerms}</span>
                      ) : <span style={{ color: 'var(--ink-3)' }}>-</span>}
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--ink-2)' }}>
                      {c.creditLimit ? formatINR(c.creditLimit) : '-'}
                    </td>
                    <td style={{ textAlign: 'center', color: 'var(--ink-2)' }}>{c._count?.contacts ?? 0}</td>
                    <td style={{ textAlign: 'center', color: 'var(--ink-2)' }}>{c._count?.deals ?? 0}</td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); roleKey === 'CRM_OPERATIONS' ? setEditing(c) : navigate(`/accounts/${c.id}`); }}
                          className="btn btn-ghost btn-sm"
                          style={{ padding: 4 }}
                          title="View / edit"
                        >
                          <Pencil size={13} />
                        </button>
                        {canCreate && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }}
                            className="btn btn-ghost btn-sm"
                            style={{ padding: 4, color: 'var(--danger)' }}
                            title="Delete"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Pagination */}
        {totalPages > 1 && (
          <div
            className="row between"
            style={{ padding: '12px 16px', borderTop: '1px solid var(--line)' }}
          >
            <div className="text-sm" style={{ color: 'var(--ink-3)' }}>
              Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
            </div>
            <div className="row gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn btn-outline btn-sm"
              >
                Previous
              </button>
              <span className="text-sm" style={{ color: 'var(--ink-3)' }}>Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn btn-outline btn-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div
          className="fixed"
          style={{
            bottom: 24,
            right: 24,
            background: 'var(--ink-0)',
            color: 'var(--ivory-0)',
            padding: '10px 16px',
            borderRadius: 'var(--r-sm)',
            boxShadow: 'var(--sh-3)',
            fontSize: 13,
            zIndex: 60,
          }}
        >
          {toast}
        </div>
      )}

      {/* Create modal */}
      <CreateCustomerModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />

      {/* Scoped styles */}
      <style>{`
        .aura-root .ivory-input {
          width: 100%;
          padding: 8px 10px;
          font-size: var(--fs-sm);
          font-family: inherit;
          color: var(--ink-0);
          background: var(--ivory-0);
          border: 1px solid var(--line);
          border-radius: var(--r-sm);
          outline: none;
          transition: border-color .12s, box-shadow .12s;
        }
        .aura-root .ivory-input:focus {
          border-color: var(--saffron-600);
          box-shadow: 0 0 0 3px var(--saffron-50);
        }
      `}</style>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Local atoms                                                        */
/* ------------------------------------------------------------------ */
